import React, { useState, useEffect } from 'react';
import { Nav, PublicNav } from '../components/Nav';
import { Box, CircularProgress } from '@mui/material';
import { useUser, useAuthStatus, useGetUserInfoQuery, useUserOnboarding } from '@features/user';
import { useStatsigClient } from '@statsig/react-bindings';
import { Lockout } from '../components/Lockout';
import { UnauthorizedError } from '../components/errors/UnauthorizedError';
import OnboardingView from './OnboardingViewRevamp';
import LoginView from './LoginView';
import SessionExpiredView from './SessionExpiredView';
import { useLocation } from 'react-router-dom';
import { useStatsigUserSync } from '@services/statsig';
// Import the front-chat feature and widget
import { FrontChat } from '@features/front-chat';
import { EncounterNavigationGuard } from '../components/guards/EncounterNavigationGuard';

const EXCEPTED_ROUTES = ['/record', '/user', '/group-invite'];

const isExceptedRoute = (pathname: string) => {
  return EXCEPTED_ROUTES.some(route => pathname.includes(route));
};

export const RootView = ({ children }: { children: any }) => {
  const { userState } = useUser();
  const authStatus = useAuthStatus();
  const shouldSkipQuery = authStatus === 'unauthenticated' || authStatus === 'initializing';
  const { isLoading: userQueryLoading } = useGetUserInfoQuery(undefined, { skip: shouldSkipQuery });
  const { onboardingSteps } = useUserOnboarding({ skip: shouldSkipQuery });
  const location = useLocation();
  const isExcepted = isExceptedRoute(location.pathname);
  const { client: statsigClient } = useStatsigClient();
  const [statsigTimeout, setStatsigTimeout] = useState(false);

  // Sync Statsig user whenever userState changes
  useStatsigUserSync();

  // Add timeout for Statsig initialization
  useEffect(() => {
    if (authStatus === 'authenticated') {
      // Only set timeout if Statsig is not ready yet
      if (statsigClient.loadingStatus !== 'Ready') {
        // Set a 5-second timeout for Statsig initialization
        const timer = setTimeout(() => {
          console.warn('Statsig initialization timeout after 5s - proceeding with default values');
          setStatsigTimeout(true);
        }, 5000);

        return () => clearTimeout(timer);
      }
    }
  }, [authStatus, statsigClient.loadingStatus]);

  const isFullScreenComponent =
    [
      '/',
      '/note/',
      '/templates',
      '/demo',
      '/patient',
      '/notes',
      '/group',
      '/patients',
      '/onboarding',
      '/dashboard',
      '/record',
      '/group-invite',
    ].some(path => window.location.pathname.startsWith(path)) || false;

  const userEmail = userState?.userInfo?.email || '';
  const paymentStatus = userState?.paymentStatus || '';

  // Determine which navigation to show
  const shouldShowFullNav = () => {
    // Show full Nav for:
    // - Fully authenticated users
    // - Locked out users (they're authenticated but payment issues)
    // - Excepted routes (special pages that always get full nav)
    return authStatus === 'authenticated' || authStatus === 'locked_out' || isExcepted;
  };

  // Check if Statsig is ready (only for authenticated users)
  // Will proceed after timeout even if Statsig fails to initialize
  const statsigReady =
    authStatus === 'authenticated'
      ? (statsigClient.loadingStatus === 'Ready' && userState?.user_id) || statsigTimeout
      : true; // Don't wait for Statsig if not authenticated

  // Show loading while checking authentication or waiting for Statsig
  if (
    authStatus === 'initializing' ||
    authStatus === 'checking' ||
    (!shouldSkipQuery && userQueryLoading) ||
    !statsigReady
  ) {
    return (
      <Box
        id="root-container"
        className="rr-mask"
        sx={{
          height: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Route to appropriate view based on auth status
  const renderMainContent = () => {
    switch (authStatus) {
      case 'unauthenticated':
        return <LoginView />;

      case 'unauthorized':
        return <SessionExpiredView />;

      case 'error':
        return (
          <UnauthorizedError message="An authentication error occurred. Please log in again." />
        );

      case 'locked_out':
        if (isExcepted) {
          return children;
        }
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Box sx={{ width: '70%' }}>
              <Lockout user_email={userEmail} payment_status={paymentStatus} />
            </Box>
          </Box>
        );

      case 'authenticated':
        // Check if user needs to complete signup
        if (!onboardingSteps['account_creation_onboarding_completed']) {
          return <OnboardingView />;
        }
        // Wrap with navigation guard to intercept encounter navigation while recording
        return <EncounterNavigationGuard>{children}</EncounterNavigationGuard>;

      default:
        console.log('Unknown auth status:', authStatus);
        return children;
    }
  };

  return (
    <Box
      id="root-container"
      className="rr-mask"
      sx={{
        maxWidth: '100vw',
        overflowX: 'scroll',
        backgroundColor:
          authStatus === 'authenticated' &&
          !onboardingSteps['account_creation_onboarding_completed']
            ? 'rgba(213, 211, 224, 0.3)'
            : 'transparent',
        height: '100dvh',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}
    >
      {shouldShowFullNav() ? <Nav /> : <PublicNav />}
      <FrontChat />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          overflowX: 'hidden',
          height: '100%',
          width: '100%',
        }}
        id="root-main"
      >
        <Box
          sx={{
            width: {
              xs: isFullScreenComponent ? '100%' : '95%',
              md: isFullScreenComponent ? '100%' : '80%',
            },
            height: '100%',
          }}
        >
          {renderMainContent()}
        </Box>
      </Box>
    </Box>
  );
};
